/**
 * 記入書類 — Step 2. List from GET /api/forms-to-fill; checkboxes + 説明 + 管理者入力.
 * Selected form codes are passed to invitation send as formCodes.
 */
import React, { useState, useEffect } from 'react';
import { DocumentCopyItem } from '../VariousDocuments/types';
import DocumentCopyModal from '../VariousDocuments/DocumentCopyModal';
import { DocumentCopyModalMode } from '../VariousDocuments/DocumentCopyModal';
import InvitationFormEmployerModal from './InvitationFormEmployerModal';
import EmploymentContractAdminModal from './employmentContract/EmploymentContractAdminModal';
import { isEmploymentContractForm, isEmploymentContractContextComplete } from './employmentContract/employmentContractAdminTypes';
import {
  formRequiresEmployerFields,
  getEmployerContextForForm,
  isEmployerContextComplete,
  type FormEmployerContext,
} from '../../invitationEmployerFields';
import './FillingDocuments.css';
import { API_BASE_URL } from '../../../../../../../config';

export interface FillingDocumentsProps {
  isOpen: boolean;
  onToggle: () => void;
  value?: DocumentCopyItem[];
  onChange?: (items: DocumentCopyItem[]) => void;
  onFormCatalogChanged?: () => void;
  employerContext?: FormEmployerContext;
  onEmployerContextChange?: (formCode: string, fields: Record<string, string>) => void;
  onEmployerContextClear?: (formCode: string) => void;
  loading?: boolean;
  error?: string | null;
  lockedCodes?: Record<string, string>;
  allowCatalogEdit?: boolean;
}

const FillingDocuments: React.FC<FillingDocumentsProps> = ({
  isOpen,
  onToggle,
  value,
  onChange,
  onFormCatalogChanged,
  employerContext = {},
  onEmployerContextChange,
  onEmployerContextClear,
  loading = false,
  error = null,
  lockedCodes = {},
  allowCatalogEdit = true,
}) => {
  const [items, setItems] = useState<DocumentCopyItem[]>(() => value ?? []);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<DocumentCopyModalMode>('edit');
  const [editingItem, setEditingItem] = useState<DocumentCopyItem | null>(null);
  const [employerModalOpen, setEmployerModalOpen] = useState(false);
  const [employerEditingItem, setEmployerEditingItem] = useState<DocumentCopyItem | null>(null);

  useEffect(() => {
    if (value !== undefined) {
      setItems(value);
    }
  }, [value]);

  const half = Math.ceil(items.length / 2);
  const displayLeft = items.slice(0, half);
  const displayRight = items.slice(half);

  const handleCheckChange = (id: string) => {
    const current = items.find((i) => i.id === id);
    if (current?.checked && lockedCodes[id]) {
      return;
    }
    const wasChecked = current?.checked ?? false;
    const next = items.map((item) =>
      item.id === id ? { ...item, checked: !item.checked } : item
    );
    setItems(next);
    onChange?.(next);
    if (wasChecked && (formRequiresEmployerFields(id) || isEmploymentContractForm(id, current?.nameJp))) {
      onEmployerContextClear?.(id);
    }
  };

  const handleExplainClick = (id: string) => {
    const item = items.find((i) => i.id === id);
    if (item) {
      setEditingItem(item);
      setModalMode('edit');
      setModalOpen(true);
    }
  };

  const handleEmployerClick = (id: string) => {
    const item = items.find((i) => i.id === id);
    if (item) {
      setEmployerEditingItem(item);
      setEmployerModalOpen(true);
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setModalMode('add');
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const handleEmployerModalClose = () => {
    setEmployerModalOpen(false);
    setEmployerEditingItem(null);
  };

  const handleModalSave = async (payload: import('../VariousDocuments/DocumentCopyModal').DocumentCopyModalSavePayload) => {
    const body = {
      nameJa: payload.nameJa,
      nameEn: payload.nameEn ?? null,
      nameVi: payload.nameVi ?? null,
      descriptionJa: payload.descriptionJa ?? null,
      descriptionEn: payload.descriptionEn ?? null,
      descriptionVi: payload.descriptionVi ?? null,
    };
    if (modalMode === 'add') {
      const res = await fetch(`${API_BASE_URL}/api/forms-to-fill`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) return;
      onFormCatalogChanged?.();
    } else if (modalMode === 'edit' && editingItem) {
      const res = await fetch(`${API_BASE_URL}/api/forms-to-fill/${encodeURIComponent(editingItem.id)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) return;
      onFormCatalogChanged?.();
    }
  };

  const handleModalDelete = async (id: string) => {
    const res = await fetch(`${API_BASE_URL}/api/forms-to-fill/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
    if (!res.ok) return;
    onFormCatalogChanged?.();
  };

  const allChecked = items.length > 0 && items.every((i) => i.checked);

  const handleHeaderCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const nextChecked = !allChecked;
    const next = items.map((item) => ({
      ...item,
      checked: item.checked && lockedCodes[item.id] ? true : nextChecked,
    }));
    setItems(next);
    onChange?.(next);
    if (!nextChecked) {
      for (const item of items) {
        if (item.checked && !lockedCodes[item.id] && formRequiresEmployerFields(item.id)) {
          onEmployerContextClear?.(item.id);
        }
      }
    }
  };

  const renderRow = (item: DocumentCopyItem) => {
    const needsEmployer =
      item.checked &&
      (formRequiresEmployerFields(item.id) || isEmploymentContractForm(item.id, item.nameJp));
    const employerFields = getEmployerContextForForm(employerContext, item.id);
    const employerComplete = isEmploymentContractForm(item.id, item.nameJp)
      ? isEmploymentContractContextComplete(employerFields)
      : isEmployerContextComplete(item.id, employerFields);
    const showIncomplete = needsEmployer && !employerComplete;
    const lockLabel = lockedCodes[item.id];
    const isLocked = Boolean(item.checked && lockLabel);

    return (
      <div key={item.id} className="filling-documents__row">
        <input
          type="checkbox"
          className="filling-documents__checkbox"
          checked={item.checked}
          disabled={isLocked}
          title={isLocked ? `${lockLabel}のため解除できません` : undefined}
          onChange={() => handleCheckChange(item.id)}
          id={`filling-doc-${item.id}`}
        />
        <label htmlFor={`filling-doc-${item.id}`} className="filling-documents__label">
          {needsEmployer && (
            <span className="filling-documents__required-mark" title="管理者入力（任意）" aria-hidden>
              ＊
            </span>
          )}
          <span className="filling-documents__label-text">{item.nameJp}</span>
          {isLocked && (
            <span className="filling-documents__lock-badge" title={lockLabel}>
              ロック
            </span>
          )}
          {showIncomplete && (
            <span className="filling-documents__incomplete-badge">未入力</span>
          )}
        </label>
        {allowCatalogEdit && (
          <button
            type="button"
            className="filling-documents__explain-btn"
            onClick={() => handleExplainClick(item.id)}
          >
            説明
          </button>
        )}
        {needsEmployer && (
          <button
            type="button"
            className={`filling-documents__employer-btn${
              employerComplete ? ' filling-documents__employer-btn--complete' : ''
            }`}
            onClick={() => handleEmployerClick(item.id)}
            title="管理者入力"
          >
            管理者入力
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="collapsible-section collapsible-section--filling">
      <button
        type="button"
        className="collapsible-section__header"
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <input
          type="checkbox"
          className="collapsible-section__checkbox"
          checked={allChecked}
          onChange={handleHeaderCheckChange}
          onClick={(e) => e.stopPropagation()}
          aria-label="すべて選択"
        />
        <span className="collapsible-section__title">記入書類</span>
        <span className={`collapsible-section__chevron ${isOpen ? 'collapsible-section__chevron--open' : ''}`}>
          ▼
        </span>
      </button>
      {isOpen && (
        <div className="collapsible-section__body collapsible-section__body--filling">
          {loading && <p className="filling-documents__hint">読み込み中…</p>}
          {error && (
            <p className="filling-documents__hint" role="alert">
              {error}
            </p>
          )}
          <p className="filling-documents__legend">
            <span className="filling-documents__required-mark" aria-hidden>＊</span>
            管理者入力がある書類（任意・後から編集可）
          </p>
          <div className="filling-documents">
            <div className="filling-documents__columns">
              <div className="filling-documents__column">
                {displayLeft.map((item) => renderRow(item))}
              </div>
              <div className="filling-documents__column">
                {displayRight.map((item) => renderRow(item))}
                {allowCatalogEdit && (
                <button type="button" className="filling-documents__add-btn" onClick={handleAddNew}>
                  新規追加....
                </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <DocumentCopyModal
        isOpen={modalOpen}
        mode={modalMode}
        item={editingItem}
        onClose={handleModalClose}
        onSave={handleModalSave}
        onDelete={handleModalDelete}
      />

      <InvitationFormEmployerModal
        isOpen={
          employerModalOpen &&
          !isEmploymentContractForm(employerEditingItem?.id, employerEditingItem?.nameJp)
        }
        formCode={employerEditingItem?.id ?? null}
        formNameJa={employerEditingItem?.nameJp ?? ''}
        context={employerContext}
        onClose={handleEmployerModalClose}
        onSave={(formCode, fields) => onEmployerContextChange?.(formCode, fields)}
      />

      <EmploymentContractAdminModal
        isOpen={
          employerModalOpen &&
          isEmploymentContractForm(employerEditingItem?.id, employerEditingItem?.nameJp)
        }
        formCode={employerEditingItem?.id ?? ''}
        formNameJa={employerEditingItem?.nameJp ?? ''}
        initialFlat={getEmployerContextForForm(employerContext, employerEditingItem?.id)}
        onClose={handleEmployerModalClose}
        onSave={(formCode, fields) => onEmployerContextChange?.(formCode, fields)}
      />
    </div>
  );
};

export default FillingDocuments;
export type { FillingDocumentItem } from './types';
